import { getRegionalConfig } from '~/src/regions/config/config';
import type { BreadcrumbDataItem } from '~/src/components/Breadcrumb/Breadcrumb';
import { getSubFilterLabel } from '~/src/utils/additionalRegionForms';

export enum CourtFormLevel {
  FILTER_CATEGORIES = 'filterCategories',
  SUB_FILTER = 'subFilter',
  PRACTICE_AREAS = 'practiceAreas',
  FORMS = 'forms',
}

export type LevelConfig = {
  itemsToRender: string[];
  childLabel: string;
  childLevel: CourtFormLevel;
};

export const buildCourtFormUrl = (
  rootRouteUrl: string,
  level: CourtFormLevel,
  filterCategory: string = '',
  subFilterValue: string = '',
): string => {
  const params = new URLSearchParams();

  if (level) {
    params.append('level', level);
  }
  if (filterCategory) {
    params.append('filterCategory', filterCategory);
  }
  if (subFilterValue) {
    params.append('subFilter', subFilterValue);
  }

  const queryString = params.toString();
  return queryString ? `${rootRouteUrl}?${queryString}` : rootRouteUrl;
};

export const getBreadcrumbData = (params: {
  rootRouteUrl: string;
  courtFormsRootUrl?: string;
  level: CourtFormLevel | string;
  filterCategory: string;
  subFilterValue: string;
}) => {
  const {
    rootRouteUrl,
    courtFormsRootUrl,
    level,
    filterCategory,
    subFilterValue,
  } = params;
  const breadcrumbData: BreadcrumbDataItem[] = [];

  if (filterCategory) {
    breadcrumbData.push({
      label: 'Court forms',
      url: courtFormsRootUrl || rootRouteUrl,
    });
    breadcrumbData.push({
      label: `${filterCategory} forms`,
      url:
        level === CourtFormLevel.SUB_FILTER
          ? ''
          : buildCourtFormUrl(
              rootRouteUrl,
              CourtFormLevel.SUB_FILTER,
              filterCategory,
            ),
    });
  }
  if (subFilterValue) {
    breadcrumbData.push({
      label: `${subFilterValue} forms`,
    });
  }

  return breadcrumbData;
};

export const getLevelConfig = (
  currentLevel: string,
  filterCategory: string,
  templateMetadata: any,
  region?: string,
): LevelConfig => {
  switch (currentLevel) {
    case CourtFormLevel.FILTER_CATEGORIES:
    case '':
    case undefined:
    default:
      return {
        itemsToRender: Object.keys(templateMetadata.filter_categories),
        childLabel: getSubFilterLabel(region),
        childLevel: CourtFormLevel.SUB_FILTER,
      };
    case CourtFormLevel.SUB_FILTER:
    case CourtFormLevel.PRACTICE_AREAS:
      return {
        itemsToRender: templateMetadata.filter_categories[filterCategory] || [],
        childLabel: 'Forms',
        childLevel: CourtFormLevel.FORMS,
      };
  }
};

export const getDrillDownPanelLinkUrl = (params: {
  item: string;
  level: CourtFormLevel | string;
  filterCategory: string;
  childLevel: CourtFormLevel;
  rootRouteUrl: string;
}): string => {
  const { item, level, filterCategory, childLevel, rootRouteUrl } = params;
  const filterCategoryParam =
    level === CourtFormLevel.FILTER_CATEGORIES || !level
      ? item
      : filterCategory;
  const subFilterParam =
    level === CourtFormLevel.SUB_FILTER ||
    level === CourtFormLevel.PRACTICE_AREAS
      ? item
      : '';

  return buildCourtFormUrl(
    rootRouteUrl,
    childLevel,
    filterCategoryParam,
    subFilterParam,
  );
};

export const getIsRegionalPdfSupported = () => {
  const regionalConfig = getRegionalConfig();
  return regionalConfig.showRegionalCourtPicker;
};
